﻿import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { User } from '@/types';
import { supabase } from '@/lib/supabase';

interface AuthContextData {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  changePassword: (password: string) => Promise<{ success: boolean; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  registerUser: (data: { name: string; email: string; password: string }) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextData | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch profile for a given auth user id
  const fetchProfile = useCallback(async (userId: string): Promise<User | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) return null;
    const profile = data as Record<string, unknown>;

    return {
      id: profile.id as string,
      name: profile.name as string,
      cpf: (profile.cpf as string) || (profile.matricula as string) || '',
      email: profile.email as string,
      setor: profile.setor as string,
      perfil: profile.perfil as 'admin' | 'colaborador',
      status: profile.status as 'ativo' | 'inativo',
      mustChangePassword: (profile.must_change_password as boolean) || false,
      createdAt: profile.created_at as string,
    };
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchProfile(session.user.id).then((profile) => {
          setUser(profile);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        fetchProfile(session.user.id).then((profile) => {
          setUser(profile);
        });
      } else {
        setUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const login = useCallback(async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (error) {
      const msg = error.message === 'Invalid login credentials'
        ? 'E-mail ou senha incorretos.'
        : error.message;
      return { success: false, error: msg };
    }

    if (data.user) {
      const profile = await fetchProfile(data.user.id);
      if (!profile) {
        await supabase.auth.signOut();
        return { success: false, error: 'Perfil não encontrado.' };
      }

      if (profile.status === 'inativo') {
        await supabase.auth.signOut();
        return { success: false, error: 'Usuário inativo. Contate o administrador.' };
      }

      setUser(profile);
      return { success: true };
    }

    return { success: false, error: 'Erro ao autenticar.' };
  }, [fetchProfile]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const changePassword = useCallback(async (password: string) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const authUser = sessionData.session?.user;

    if (!authUser) {
      return { success: false, error: 'Usuário não autenticado.' };
    }

    const { error: authError } = await supabase.auth.updateUser({ password });
    if (authError) {
      return { success: false, error: authError.message };
    }

    const { error: profileError } = await (supabase.from('profiles') as any)
      .update({ must_change_password: false })
      .eq('id', authUser.id);

    if (profileError) {
      return { success: false, error: profileError.message };
    }

    const profile = await fetchProfile(authUser.id);
    setUser(profile);

    return { success: true };
  }, [fetchProfile]);

  const forgotPassword = useCallback(async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/login`,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  }, []);

  const registerUser = useCallback(async (data: {
    name: string;
    email: string;
    password: string;
  }) => {
    const { data: result, error } = await supabase.functions.invoke('manage-users', {
      body: {
        action: 'create',
        ...data,
        perfil: 'colaborador',
        status: 'ativo',
        forcePasswordReset: false,
      },
    });

    if (error) {
      const context = (error as { context?: Response }).context;
      if (context) {
        try {
          const body = await context.clone().json();
          if (body?.error) {
            return { success: false, error: body.error };
          }
        } catch {
          // Keep the default Supabase message when the response body is unavailable.
        }
      }
      return { success: false, error: error.message || 'Erro ao criar usuário.' };
    }

    if (result.error) {
      return { success: false, error: result.error };
    }

    return { success: true };
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, changePassword, forgotPassword, registerUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
}



